import React, { useState } from 'react';

export default function MeetingModal({ show, onClose, meetingForm, setMeetingForm, onSubmit, users }) {
  const [participantSearch, setParticipantSearch] = useState('');
  if (!show) return null;

  const filteredUsers = participantSearch.trim().length > 0 ? users.filter(u =>
    u.full_name.toLowerCase().includes(participantSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(participantSearch.toLowerCase()) ||
    (u.department || '').toLowerCase().includes(participantSearch.toLowerCase())
  ).slice(0, 8) : [];

  // Generate 15-min interval time slots
  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      timeSlots.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` });
    }
  }

  // Extract date and time from scheduled_at
  const scheduledDate = meetingForm.scheduled_at ? meetingForm.scheduled_at.split('T')[0] : '';
  const scheduledTime = meetingForm.scheduled_at ? meetingForm.scheduled_at.split('T')[1]?.substring(0, 5) : '';

  const handleDateChange = (date) => {
    const time = scheduledTime || '09:00';
    setMeetingForm({ ...meetingForm, scheduled_at: `${date}T${time}` });
  };

  const handleTimeChange = (time) => {
    const date = scheduledDate || new Date().toISOString().split('T')[0];
    setMeetingForm({ ...meetingForm, scheduled_at: `${date}T${time}` });
  };

  // Min date = today
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Schedule Meeting</h3>
              <p className="text-white/70 text-xs">Send invites to your team</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4 overflow-y-auto" style={{ maxHeight: '75vh' }}>

          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Meeting Title *</label>
            <input type="text" placeholder="e.g. Weekly Sync" value={meetingForm.title}
              onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })}
              className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Description</label>
            <textarea placeholder="Agenda or notes..." value={meetingForm.description}
              onChange={e => setMeetingForm({ ...meetingForm, description: e.target.value })} rows={2}
              className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none transition"/>
          </div>

          {/* Meeting Link */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Meeting Link</label>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-1.5">Paste your Google Meet, Zoom, or Teams link. Leave as-is to use your Qtalk Meet room.</p>
            <div className="relative">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500 flex-shrink-0">
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
              <input type="url" value={meetingForm.meet_link}
                onChange={e => setMeetingForm({ ...meetingForm, meet_link: e.target.value })}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 transition font-mono"/>
            </div>
            {meetingForm.meet_link && (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-xs text-teal-600 dark:text-teal-400">✓ Join link set</span>
                <button type="button" onClick={() => setMeetingForm({ ...meetingForm, meet_link: '' })}
                  className="text-xs text-red-400 hover:text-red-500">Clear</button>
              </div>
            )}
          </div>

          {/* Date + Time + Duration */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date & Time *</label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              {/* Date picker */}
              <div className="relative">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <input type="date" value={scheduledDate} min={today}
                  onChange={e => handleDateChange(e.target.value)}
                  className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
              </div>
              {/* Time dropdown - 15 min intervals */}
              <div className="relative">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <select value={scheduledTime} onChange={e => handleTimeChange(e.target.value)}
                  className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition appearance-none">
                  <option value="">Select time</option>
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Duration</label>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {[15, 30, 45, 60, 90, 120].map(d => (
                <button type="button" key={d}
                  onClick={() => setMeetingForm({ ...meetingForm, duration_minutes: d })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    Number(meetingForm.duration_minutes) === d
                      ? 'text-white' : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  style={Number(meetingForm.duration_minutes) === d ? { background: 'linear-gradient(135deg,#0d9488,#2563eb)' } : {}}>
                  {d < 60 ? `${d}m` : `${d / 60}h${d > 60 ? '30m' : ''}`}
                </button>
              ))}
            </div>
          </div>

          {/* Repeat */}
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Repeat</label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              <select value={meetingForm.recurrence_rule || 'none'}
                onChange={e => setMeetingForm({ ...meetingForm, recurrence_rule: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition appearance-none">
                <option value="none">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              {(meetingForm.recurrence_rule && meetingForm.recurrence_rule !== 'none') ? (
                <input type="date" value={meetingForm.recurrence_until || ''} min={scheduledDate || today}
                  onChange={e => setMeetingForm({ ...meetingForm, recurrence_until: e.target.value })}
                  title="Repeat until"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
              ) : <div/>}
            </div>
            {(meetingForm.recurrence_rule && meetingForm.recurrence_rule !== 'none') && (
              <p className="text-xs text-gray-400 mt-1">Repeats {meetingForm.recurrence_rule} until {meetingForm.recurrence_until || 'the next 6 months'}</p>
            )}
          </div>

          {/* Participants */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Participants</label>
              {meetingForm.participant_ids.length > 0 && (
                <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">{meetingForm.participant_ids.length} selected</span>
              )}
            </div>
            <div className="relative mb-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input type="text" placeholder="Search by name, email or department..."
                value={participantSearch} onChange={e => setParticipantSearch(e.target.value)}
                className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
            </div>

            {/* Selected participants chips */}
            {meetingForm.participant_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {meetingForm.participant_ids.map(uid => {
                  const u = users.find(x => x.id === uid);
                  return u ? (
                    <span key={uid} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-teal-700 dark:text-teal-300"
                      style={{ background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.2)' }}>
                      {u.full_name.split(' ')[0]}
                      <button type="button" onClick={() => setMeetingForm({ ...meetingForm, participant_ids: meetingForm.participant_ids.filter(id => id !== uid) })}
                        className="text-teal-500 hover:text-red-400 transition ml-0.5">×</button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Search results */}
            {filteredUsers.length > 0 && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {filteredUsers.map(u => {
                  const isSelected = meetingForm.participant_ids.includes(u.id);
                  return (
                    <button type="button" key={u.id}
                      onClick={() => setMeetingForm({
                        ...meetingForm,
                        participant_ids: isSelected
                          ? meetingForm.participant_ids.filter(id => id !== u.id)
                          : [...meetingForm.participant_ids, u.id]
                      })}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition text-sm ${
                        isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}>
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                        {u.avatar_url?.startsWith('/uploads/')
                          ? <img src={`https://qtalk.qualtechedge.in${u.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                          : u.full_name[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 dark:text-gray-100 truncate text-xs">{u.full_name}</p>
                        <p className="text-gray-400 truncate" style={{fontSize:'10px'}}>{u.designation || u.department || u.email}</p>
                      </div>
                      {isSelected && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submit */}
          <button type="submit"
            disabled={!meetingForm.title || !meetingForm.scheduled_at}
            className="w-full py-3 rounded-xl font-bold text-white text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
            📅 Schedule & Send Invites
          </button>
        </form>
      </div>
    </div>
  );
}
